import React from 'react';
import { cn } from '../../lib/cn';
import { Button } from '../Button/Button';
import { PeriodSelector } from '../PeriodSelector/PeriodSelector';
import type { PeriodValue } from '../../lib/period';
import type { RcFunnelStage, RcReportBase, RcReportData, RcReportStep } from '../../recall-campaigns/types';

export interface RcReportViewProps {
  data: RcReportData;
  /** The chosen period (spec 41). */
  period: PeriodValue;
  onPeriodChange: (value: PeriodValue) => void;
  /** ISO start + its label, for the custom-range floor. */
  startedISO?: string;
  startedLabel?: string;
  onBack?: () => void;
  onSetup?: () => void;
  onChangeSequence?: () => void;
}

function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('rounded-[var(--r-xl)] border border-[var(--border-default)] bg-[var(--surface-card)] p-4 shadow-[var(--shadow-card)]', className)}>
      {children}
    </div>
  );
}

function SectionHeading({ n, title, sub, action }: { n: string; title: string; sub: string; action?: React.ReactNode }) {
  return (
    <div className="mb-2.5 flex items-start justify-between gap-3">
      <div className="flex items-baseline gap-2.5">
        <span className="font-[var(--font-mono)] text-[11px] font-bold text-[var(--text-muted)]">{n}</span>
        <h2 className="m-0 text-[15px] font-bold text-[var(--text-primary)]">{title}</h2>
        <span className="text-[12px] text-[var(--text-muted)]">{sub}</span>
      </div>
      {action}
    </div>
  );
}

function FunnelStage({ s, max }: { s: RcFunnelStage; max: number }) {
  const pct = Math.max(6, Math.round((s.value / max) * 100));
  return (
    <div className="py-2">
      <div className="flex items-baseline justify-between text-[12.5px]">
        <span className={cn('inline-flex items-center gap-1.5 font-semibold', s.emphasis ? 'text-[var(--money-green)]' : 'text-[var(--text-primary)]')}>
          {s.label}
          {s.rightNow ? (
            <span className="rounded-[6px] border border-[var(--border-default)] bg-[var(--surface-sunken)] px-1.5 py-px font-[var(--font-mono)] text-[9px] font-bold uppercase tracking-[0.04em] text-[var(--text-muted)]">
              Right now
            </span>
          ) : null}
        </span>
        <span className="text-[var(--text-secondary)]">
          <span className="font-[var(--font-mono)] font-bold text-[var(--text-primary)]">{s.value.toLocaleString()}</span>
          {s.pct ? <span className="ml-1.5 font-[var(--font-mono)] text-[11px] text-[var(--text-muted)]">{s.pct}</span> : null}
        </span>
      </div>
      <div className="mt-1 h-2 overflow-hidden rounded-[var(--radius-bar)] bg-[var(--surface-sunken)]">
        <div
          className="h-full rounded-[var(--radius-bar)]"
          style={{ width: `${String(pct)}%`, background: s.emphasis ? 'var(--money-green)' : 'var(--brand-indigo)' }}
        />
      </div>
      {s.note ? <div className="mt-1 text-[11px] text-[var(--text-muted)]">{s.note}</div> : null}
    </div>
  );
}

function StepBlock({ s }: { s: RcReportStep }) {
  return (
    <div className="border-t border-[var(--border-default)] py-2.5">
      <div className="grid grid-cols-[28px_54px_90px_60px_60px_60px_58px] items-center gap-3 px-1 text-[12px]">
        <span className="font-[var(--font-mono)] text-[11px] font-bold text-[var(--text-secondary)]">{s.n}</span>
        <span className="font-[var(--font-mono)] text-[11px] text-[var(--text-muted)]">{s.day}</span>
        <span className="font-semibold text-[var(--text-primary)]">{s.channel}</span>
        <span className="text-right font-[var(--font-mono)] text-[var(--text-secondary)]">{s.sent}</span>
        <span className="text-right font-[var(--font-mono)] text-[var(--text-secondary)]">{s.reached}</span>
        <span className="text-right font-[var(--font-mono)] text-[var(--text-secondary)]">{s.booked}</span>
        <span className="text-right font-[var(--font-mono)] text-[13px] font-bold text-[var(--money-green)]">{s.ofReached}</span>
      </div>
      <p className="mt-1.5 px-1 text-[11px] leading-[1.5] text-[var(--text-muted)]">{s.wait}</p>
    </div>
  );
}

function BaseRow({ b }: { b: RcReportBase }) {
  return (
    <div className="flex items-baseline gap-3 border-t border-[var(--border-default)] px-1 py-2 text-[12px]">
      <span className="w-[120px] shrink-0 font-semibold text-[var(--text-primary)]">{b.label}</span>
      <span className="w-[110px] shrink-0 font-[var(--font-mono)] text-[13px] font-bold text-[var(--text-primary)]">{b.value}</span>
      <span className="text-[11.5px] text-[var(--text-muted)]">{b.note}</span>
    </div>
  );
}

export function RcReportView({ data, period, onPeriodChange, startedISO = '2026-03-03', startedLabel = '3 Mar 2026', onBack, onSetup, onChangeSequence }: RcReportViewProps) {
  const funnelMax = data.funnel[0]?.value ?? 1;

  return (
    <div className="cal-fade mx-auto min-h-0 w-full max-w-[1084px] flex-1 overflow-y-auto px-[30px] pb-16 pt-5">
      {/* header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="pc-eyebrow text-[var(--text-muted)]">Recall › {data.name} › Report</div>
          <h1 className="m-0 mt-0.5 font-[var(--font-display)] text-[24px] font-semibold tracking-[-0.02em] text-[var(--text-primary)]">
            {data.name}
          </h1>
          <p className="m-0 mt-0.5 text-[12.5px] text-[var(--text-secondary)]">{data.cycle}</p>
        </div>
        <div className="flex items-center gap-2">
          <PeriodSelector variant="report" value={period} onChange={onPeriodChange} startedISO={startedISO} startedLabel={startedLabel} />
          <Button variant="ghost" size="sm" iconLeft="arrow-l" onClick={onBack}>
            All recalls
          </Button>
          <Button variant="secondary" size="sm" iconLeft="cog" onClick={onSetup}>
            Setup
          </Button>
        </div>
      </div>

      {/* hero funnel */}
      <Card className="mt-5 border-l-[3px] border-l-[var(--brand-indigo)]">
        <div className="pc-eyebrow text-[var(--text-secondary)]">From the audience to the chair</div>
        <p className="m-0 mt-0.5 text-[11.5px] text-[var(--text-muted)]">
          Counts patients who <span className="font-semibold text-[var(--text-secondary)]">attended</span>, not just booked.
        </p>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="font-[var(--font-mono)] text-[32px] font-bold tracking-[-0.03em] text-[var(--money-green)]">
            {data.heroProduction}
          </span>
          <span className="text-[12px] text-[var(--text-secondary)]">{data.heroSub}</span>
        </div>
        <div className="mt-3 border-t border-[var(--border-default)] pt-2">
          {data.funnel.map((s) => (
            <FunnelStage key={s.key} s={s} max={funnelMax} />
          ))}
        </div>
      </Card>

      {/* 01 every step */}
      <Card className="mt-3">
        <SectionHeading
          n="01"
          title="Every step"
          sub="Sent, reached, booked — and what share of the people it reached said yes."
          action={
            <button type="button" onClick={onChangeSequence} className="text-[12px] font-semibold text-[var(--brand-indigo)] hover:underline">
              Change the sequence →
            </button>
          }
        />
        <div className="grid grid-cols-[28px_54px_90px_60px_60px_60px_58px] gap-3 px-1 pb-1 font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">
          <span />
          <span>Day</span>
          <span>Channel</span>
          <span className="text-right">Sent</span>
          <span className="text-right">Reached</span>
          <span className="text-right">Booked</span>
          <span className="text-right">Of reached</span>
        </div>
        {data.steps.map((s) => (
          <StepBlock key={s.n} s={s} />
        ))}
      </Card>

      {/* 02 what every number is out of */}
      <Card className="mt-3">
        <SectionHeading n="02" title="What every number is out of" sub="Every rate states its base, so no two figures can disagree." />
        {data.bases.map((b) => (
          <BaseRow key={b.label} b={b} />
        ))}
      </Card>
    </div>
  );
}
